"use client";

import { useCallback, useState, useRef, useEffect } from "react";
import { useApiFetcher } from "./useApiFetcher";

/**
 * @template T
 * @callback UseRequestFunction
 * @param {string} url
 * @param {RequestInit} [init]
 * @param {object} [options]
 * @param {boolean} [options.abortPendingRequest]
 * @returns {Promise<import("./useApiFetcher").ApiFetcherReturn<T>>}
 */

/**
 * @typedef {object} UseRequestOptions
 * @prop {(input: RequestInfo | URL, init?: RequestInit) => Promise<any>} [fetcher]
 */

/**
 * @template T
 * @typedef {object} UseRequest
 * @prop {UseRequestFunction<T>} request
 * @prop {boolean} loading
 */

/**
 * A custom hook to manage API requests with automatic cancellation of previous requests when a new one is sent.
 * @template [T=any]
 * @param {UseRequestOptions} [options]
 * @returns {UseRequest<T>}
 */
export function useRequest(options) {
  const [loading, setLoading] = useState(false);

  const controllerRef = useRef(/** @type {AbortController | null} */ (null));

  const { apiFetcher } = useApiFetcher();

  useEffect(() => {
    return () => {
      // Abort any ongoing request on unmount
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  /** @type {UseRequestFunction<T>} */
  const request = useCallback(
    (url, init = {}, requestOptions) => {
      // Abort the previous request if it exists
      if (requestOptions?.abortPendingRequest && controllerRef.current) {
        controllerRef.current.abort();
      }

      // Create a new AbortController and store it
      const controller = new AbortController();
      controllerRef.current = controller;

      setLoading(true);

      return (options?.fetcher ?? apiFetcher)(url, {
        ...init,
        signal: controller.signal,
      })
        .catch((error) => {
          // Don't rethrow if the request was aborted
          if (error.name === "AbortError") {
            return;
          }
          throw error;
        })
        .finally(() => {
          // Clear the controller reference and reset loading state
          controllerRef.current = null;
          setLoading(false);
        });
    },
    [apiFetcher, options?.fetcher],
  );

  return {
    loading,
    request,
  };
}

export default useRequest;
